import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "reactstrap";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";

import { systemsColors } from "Pages/Admin/ClientMap/Components/systemsColors";
import Legend from "Pages/Admin/ClientMap/Components/Legend/Legend";
import styles from "Pages/Admin/ClientMap/ClientMap.module.css";
import usePageTitle from "Hooks/usePageTitle";

export interface IColorMap {
    [key: string]: string;
}

interface ICompany {
    activation_code: null | string;
    address: string;
    company_id: string;
    company_users: null | any;
    created_date: string;
    enabled: boolean;
    latitude: number;
    longitude: number;
    modified_date: string;
    name: string;
    status: string;
    systems: string;
}

/**
 * System → CSS hex. Mirrors systemsColors' bootstrap class names.
 * Inlined as hex because Leaflet markers take a real color, not a bs class.
 */
const SYSTEM_HEX: IColorMap = {
    psft: "#0d6efd",   // bootstrap primary
    xero: "#212529",   // bootstrap dark
    cmic: "#198754",   // bootstrap success
    mrix: "#dc3545",   // bootstrap danger
    jde: "#ffc107",    // bootstrap warning
    sage: "#0dcaf0",   // bootstrap info
    other: "#6c757d",  // bootstrap secondary
};

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

/**
 * Inject Leaflet CSS + JS once (idempotent). Resolves when window.L is ready.
 */
function ensureLeafletLoaded(): Promise<any> {
    if (typeof window === "undefined") return Promise.reject("no window");
    if ((window as any).L) return Promise.resolve((window as any).L);

    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = LEAFLET_CSS;
        document.head.appendChild(link);
    }
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve((window as any).L));
            existing.addEventListener("error", reject);
            return;
        }
        const script = document.createElement("script");
        script.src = LEAFLET_JS;
        script.async = true;
        script.onload = () => resolve((window as any).L);
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

export default function ClientMap() {
    const [data, setData] = useState<ICompany[]>();
    const [filter, setFilter] = useState<string>("");
    const [leafletReady, setLeafletReady] = useState(false);

    usePageTitle("Client Map");
    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    const mapEl = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<any>(null);
    /** Track markers grouped by system so the filter useEffect can toggle layer membership. */
    const markersBySystem = useRef<{ [system: string]: any[] }>({});
    const allMarkers = useRef<any[]>([]);

    // Load Leaflet from CDN (idempotent — won't re-inject on re-render).
    useEffect(() => {
        let cancelled = false;
        ensureLeafletLoaded()
            .then(() => {
                if (!cancelled) setLeafletReady(true);
            })
            .catch(() => {
                newAlert({
                    message: "Failed to load Leaflet map library.",
                    color: "danger",
                });
            });
        return () => {
            cancelled = true;
        };
    }, [newAlert]);

    // Get the map markers from the existing API.
    useEffect(() => {
        let ignore = false;
        async function getMarkers() {
            const response = await get("/api/client_map/markers");
            if (response.ok && !ignore) {
                const json = await response.json();
                setData(json);
            }
        }
        getMarkers();
        return () => {
            ignore = true;
        };
    }, [get]);

    /** Init Leaflet map once Leaflet is loaded and the container exists. */
    useEffect(() => {
        if (!leafletReady) return;
        if (mapInstance.current) return;
        if (!mapEl.current) return;
        const L = (window as any).L;

        mapInstance.current = L.map(mapEl.current).setView(
            [39.725194, -105.175531],
            3,
        );
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 18,
        }).addTo(mapInstance.current);
        // In case the container resized after the map was initialised.
        setTimeout(() => mapInstance.current?.invalidateSize(), 100);
    }, [leafletReady]);

    /** Resolve a company's primary non-Procore system, falling back to "other". */
    const primarySystem = (systems: string | undefined): string => {
        if (!systems) return "other";
        const known = systemsColors.map((s) => s.name);
        const parts = systems
            .toLowerCase()
            .split("|")
            .filter((s) => s !== "procore");
        return parts.find((p) => known.includes(p)) ?? "other";
    };

    /** Build / rebuild markers whenever the data + map are both ready. */
    useEffect(() => {
        if (!leafletReady) return;
        if (!mapInstance.current) return;
        if (!data) return;
        const L = (window as any).L;

        // Wipe any prior markers (e.g. on re-fetch).
        allMarkers.current.forEach((m) => mapInstance.current.removeLayer(m));
        allMarkers.current = [];
        markersBySystem.current = {};

        data.forEach((d) => {
            if (!d.latitude || !d.longitude) return;
            const system = primarySystem(d.systems);
            const color = SYSTEM_HEX[system] ?? SYSTEM_HEX.other;
            const marker = L.circleMarker([d.latitude, d.longitude], {
                radius: 8,
                color: "#222",
                weight: 1,
                fillColor: color,
                fillOpacity: 0.85,
            });
            const safeName = (d.name || "").replace(/</g, "&lt;");
            const safeAddr = (d.address || "").replace(/</g, "&lt;");
            marker.bindPopup(
                `<strong>${safeName}</strong><br/>` +
                    `<code>${d.company_id}</code> · ${system}<br/>` +
                    (safeAddr ? `${safeAddr}<br/>` : "") +
                    `<em>${d.status || ""}${d.enabled ? "" : " (disabled)"}</em>`,
            );
            marker.addTo(mapInstance.current);
            allMarkers.current.push(marker);
            if (!markersBySystem.current[system]) markersBySystem.current[system] = [];
            markersBySystem.current[system].push(marker);
        });

        // Fit to all markers on load.
        if (allMarkers.current.length > 0) {
            const group = L.featureGroup(allMarkers.current);
            mapInstance.current.fitBounds(group.getBounds().pad(0.1));
        }
    }, [data, leafletReady]);

    /** Toggle marker visibility on filter change. */
    useEffect(() => {
        if (!leafletReady) return;
        if (!mapInstance.current) return;
        // Remove all then re-add the visible ones.
        allMarkers.current.forEach((m) => mapInstance.current.removeLayer(m));
        const visible =
            filter === ""
                ? allMarkers.current
                : markersBySystem.current[filter] ?? [];
        visible.forEach((m: any) => m.addTo(mapInstance.current));
    }, [filter, leafletReady]);

    /** isShown is still passed to Legend for the brightness/disabled feedback. */
    const isShown = (
        f: string,
        system: string,
        possibleSystems: string[],
    ) => {
        if (f === "") return true;
        if (f === system) return true;
        if (f === "other" && !possibleSystems.includes(system)) return true;
        return false;
    };

    const summary = useMemo(() => {
        const count = data?.filter((d) => d.latitude && d.longitude).length ?? 0;
        return `${count} companies with coordinates`;
    }, [data]);

    return (
        <Container className={styles.container}>
            <h2 className={styles.title}>Client Map</h2>
            <p className={styles.summary}>{summary} · click a legend chip to filter by system</p>
            <div className={styles.layout}>
                <div ref={mapEl} className={styles.map} />
                <Legend filter={filter} onSetFilter={setFilter} isShown={isShown} />
            </div>
        </Container>
    );
}
